import { motion, useReducedMotion } from 'framer-motion'

const gDrift =
  '[transform-box:view-box] origin-[50%_45%] will-change-transform animate-service-hero-lines-drift' as const

type LineSpec = {
  id: string
  d: string
  className: string
  strokeWidth: number
  /** Fraction of path length for the lit segment (rest is gap); sum with gap = 1 via pathLength. */
  dash: number
  /** Seconds for one full travel along the path */
  flowDuration: number
  /** Stagger flow start */
  flowDelay: number
  reverse?: boolean
}

const LINES: LineSpec[] = [
  {
    id: 'l1',
    d:
      'M -280 268 C 140 210 420 320 760 272 C 1040 236 1240 298 1520 258 C 1680 238 1820 288 2040 252',
    className: 'text-white/[0.055]',
    strokeWidth: 1.05,
    dash: 0.14,
    flowDuration: 18,
    flowDelay: 0,
  },
  {
    id: 'l2',
    d:
      'M -240 398 C 120 348 460 448 820 392 C 1080 358 1280 422 1580 378 C 1740 358 1880 408 2060 372',
    className: 'text-white/[0.044]',
    strokeWidth: 1,
    dash: 0.11,
    flowDuration: 22,
    flowDelay: 0.85,
    reverse: true,
  },
  {
    id: 'l3',
    d:
      'M -300 518 C 100 468 520 558 880 508 C 1120 478 1320 538 1620 492 C 1760 472 1900 522 2080 482',
    className: 'text-white/[0.036]',
    strokeWidth: 1,
    dash: 0.12,
    flowDuration: 20,
    flowDelay: 1.7,
  },
  {
    id: 'l4',
    d:
      'M -260 648 C 160 598 500 688 840 632 C 1100 598 1300 668 1600 612 C 1760 588 1860 638 2020 598',
    className: 'text-white/[0.028]',
    strokeWidth: 0.95,
    dash: 0.1,
    flowDuration: 24,
    flowDelay: 0.55,
    reverse: true,
  },
]

function FlowingPath({ line }: { line: LineSpec }) {
  const reduceMotion = useReducedMotion()
  const gap = 1 - line.dash
  const dashArray = `${line.dash} ${gap}`

  if (reduceMotion) {
    return (
      <path
        className={line.className}
        d={line.d}
        fill="none"
        stroke="currentColor"
        strokeWidth={line.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    )
  }

  const offsetEnd = line.reverse ? 1 : -1

  return (
    <>
      {/* Faint full track so curves stay visible between pulses */}
      <path
        className={`${line.className} opacity-[0.22]`}
        d={line.d}
        fill="none"
        stroke="currentColor"
        strokeWidth={line.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <motion.path
        className={line.className}
        d={line.d}
        fill="none"
        stroke="currentColor"
        strokeWidth={line.strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        pathLength={1}
        strokeDasharray={dashArray}
        initial={{ strokeDashoffset: 0 }}
        animate={{ strokeDashoffset: offsetEnd }}
        transition={{
          strokeDashoffset: {
            duration: line.flowDuration,
            repeat: Infinity,
            ease: 'linear',
            delay: line.flowDelay,
          },
        }}
      />
    </>
  )
}

function LineGroup({
  lines,
  delayClass,
}: {
  lines: LineSpec[]
  delayClass?: string
}) {
  return (
    <g className={`${gDrift} ${delayClass ?? ''}`}>
      {lines.map((line) => (
        <g key={line.id}>
          <FlowingPath line={line} />
        </g>
      ))}
    </g>
  )
}

export default function ServiceHeroVectorLines() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      <svg
        className="absolute left-1/2 top-1/2 min-h-[120%] min-w-[120%] -translate-x-1/2 -translate-y-1/2"
        viewBox="0 0 1600 900"
        fill="none"
        preserveAspectRatio="xMidYMid slice"
      >
        <defs>
          <filter
            id="service-hero-line-soft"
            x="-12%"
            y="-12%"
            width="124%"
            height="124%"
            colorInterpolationFilters="sRGB"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="0.55" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <g filter="url(#service-hero-line-soft)">
          <LineGroup lines={LINES.slice(0, 2)} />
          <LineGroup lines={LINES.slice(2, 4)} delayClass="[animation-delay:-38s]" />
        </g>
      </svg>
    </div>
  )
}
